import React, { useState, useEffect } from 'react';
import { useNavigation } from '../navigation';
import PharmaHistoryTable from './Pharma-history-table';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

const PurchaseHistory: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedPurchase, setSelectedPurchase] = useState<number | null>(null);
  const [purchasesData, setPurchasesData] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [apiError, setApiError] = useState('');
  const [expandedPurchaseId, setExpandedPurchaseId] = useState<number | null>(null);
  const [purchaseMedications, setPurchaseMedications] = useState<any>(null);
  const [isLoadingMedications, setIsLoadingMedications] = useState(false);
  const [medicationsError, setMedicationsError] = useState('');
  const [receivingPurchaseId, setReceivingPurchaseId] = useState<number | null>(null);
  const [receiveError, setReceiveError] = useState('');
  const [receiveResponse, setReceiveResponse] = useState<any>(null);
  const [showReceiveResponse, setShowReceiveResponse] = useState(false);
  const [responseCountdown, setResponseCountdown] = useState(8);
  const [responseTimer, setResponseTimer] = useState<NodeJS.Timeout | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [analyticsData, setAnalyticsData] = useState<any | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [csvDownloadError, setCsvDownloadError] = useState<string | null>(null);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatYMD = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  /** Normalize to YYYY-MM-DD for API query params (e.g. CSV export). */
  const toYYYYMMDDQueryParam = (dateStr: string): string => {
    const v = dateStr.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    return v;
  };

  const toDDMMYYYY = (ymd: string) => {
    if (!ymd) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`;
    }
    return ymd;
  };

  const getEffectiveDatesAnalytics = () => {
    const today = new Date();
    const todayDDMMYYYY = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    const from = customFromDate ? toDDMMYYYY(customFromDate) : '01-01-2000';
    const to = customToDate ? toDDMMYYYY(customToDate) : todayDDMMYYYY;
    return { from, to };
  };
  const getEffectiveDatesPurchases = () => {
    const today = new Date();
    const todayDDMMYYYY = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    const start_date = customFromDate ? toDDMMYYYY(customFromDate) : '01-01-2000';
    const end_date = customToDate ? toDDMMYYYY(customToDate) : todayDDMMYYYY;
    return { start_date, end_date };
  };

  /** YYYY-MM-DD for CSV export (`from_date` / `to_date` query params). */
  const getEffectiveDatesPurchasesCsv = () => {
    const todayYMD = formatYMD(new Date());
    const from = customFromDate ? toYYYYMMDDQueryParam(customFromDate) : '2000-01-01';
    const to = customToDate ? toYYYYMMDDQueryParam(customToDate) : todayYMD;
    return { from, to };
  };

  const applyDatePreset = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    setCustomFromDate(formatYMD(from));
    setCustomToDate(formatYMD(to));
    setCurrentPage(1);
  };

  const fetchAnalytics = async () => {
    try {
      setIsLoadingAnalytics(true);
      setAnalyticsError(null);
      const authToken = getAuthToken();
      if (!authToken) {
        setAnalyticsError('Authentication token not found. Please log in again.');
        setIsLoadingAnalytics(false);
        return;
      }
      const { from, to } = getEffectiveDatesAnalytics();
      const params = new URLSearchParams({
        from_date: from,
        to_date: to
      });
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/reporting/analytics/purchases?${params.toString()}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setAnalyticsData(data);
    } catch (error: any) {
      setAnalyticsError(error?.message || 'Failed to load analytics.');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const downloadPurchaseOrdersCsv = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setCsvDownloadError('Please log in again to download.');
      setTimeout(() => setCsvDownloadError(null), 4000);
      return;
    }
    setCsvDownloadError(null);
    setIsDownloadingCsv(true);
    try {
      const { from, to } = getEffectiveDatesPurchasesCsv();
      const params = new URLSearchParams({
        from_date: from,
        to_date: to
      });
      const response = await fetch(
        `https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/purchase_orders/csv?${params.toString()}`,
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            'ngrok-skip-browser-warning': 'true',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      if (!response.ok) {
        let detail = '';
        try {
          detail = await response.text();
        } catch { /* ignore */ }
        throw new Error(detail || `Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const cd = response.headers.get('Content-Disposition');
      let filename = 'purchase_orders.csv';
      if (cd) {
        const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;\s]+)/i.exec(cd);
        if (m) {
          const raw = (m[1] || m[2] || m[3] || '').trim();
          if (raw) filename = decodeURIComponent(raw.replace(/^["']|["']$/g, ''));
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Could not download CSV';
      setCsvDownloadError(message);
      setTimeout(() => setCsvDownloadError(null), 5000);
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  // Fetch purchases data from API with pagination
  const fetchPurchasesData = async (page: number = currentPage, itemsPerPage: number = perPage) => {
    try {
      setIsLoadingData(true);
      setApiError('');
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        setApiError('Authentication token not found. Please log in again.');
        setIsLoadingData(false);
        return;
      }
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: itemsPerPage.toString()
      });
      const { start_date, end_date } = getEffectiveDatesPurchases();
      params.set('start_date', start_date);
      params.set('end_date', end_date);
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/?${params.toString()}`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle paginated response structure
      if (data && Array.isArray(data)) {
        // If response is a simple array, treat as page 1 with all items
        setPurchasesData(data);
        setTotalPages(1);
        setTotalItems(data.length);
      } else if (data && data.items && Array.isArray(data.items)) {
        // Paginated response with items array
        setPurchasesData(data.items);
        setTotalPages(data.total_pages || data.totalPages || Math.ceil((data.total || data.items.length) / itemsPerPage));
        setTotalItems(data.total || data.items.length);
      } else if (data && data.purchases && Array.isArray(data.purchases)) {
        // Alternative structure with purchases array
        setPurchasesData(data.purchases);
        setTotalPages(data.pages || data.total_pages || data.totalPages || 1);
        setTotalItems(data.total || data.purchases.length);
      } else if (data && Array.isArray(data.data)) {
        // Response with data array
        setPurchasesData(data.data);
        setTotalPages(data.total_pages || data.totalPages || Math.ceil((data.total || data.data.length) / itemsPerPage));
        setTotalItems(data.total || data.data.length);
      } else {
        setPurchasesData([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      
      // Check for different types of errors
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        setApiError(`
          CORS Error: Unable to connect to the purchases API.
          
          This error occurs because the server doesn't allow requests from localhost:3000.
          
          Possible solutions:
          1. Server needs CORS headers: Access-Control-Allow-Origin: http://localhost:3000
          2. Check if the server is running and accessible
          3. Verify the API endpoint URL is correct
          4. Contact the server administrator to fix CORS configuration
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/
          
          For now, the component will show sample data.
        `);
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setApiError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setApiError('Access denied. You do not have permission to view purchases data.');
      } else {
        setApiError(`Failed to load purchases data: ${error.message}`);
      }
      
      // Fallback to sample data if API fails
      setPurchasesData(samplePurchasesData);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch purchase medications
  const fetchPurchaseMedications = async (purchaseId: number) => {
    try {
      setIsLoadingMedications(true);
      setMedicationsError('');
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        setMedicationsError('Authentication token not found. Please log in again.');
        setIsLoadingMedications(false);
        return;
      }
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseId}/info`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setPurchaseMedications(data);
    } catch (error) {
      
      // Check for different types of errors
      if (error.message.includes('ERR_CERT_COMMON_NAME_INVALID') || 
          error.message.includes('ERR_CERT_AUTHORITY_INVALID') ||
          error.message.includes('ERR_SSL_PROTOCOL_ERROR') ||
          error.message.includes('certificate')) {
        setMedicationsError('SSL Certificate Error: The server certificate is invalid. Please contact the administrator or try using HTTP instead of HTTPS.');
      } else if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        setMedicationsError(`
          CORS Error: Unable to connect to the medications API.
          
          Possible solutions:
          1. Check if your server is running
          2. Verify CORS is properly configured on the server
          3. Ensure the API endpoint is accessible
          4. Try refreshing the page
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/{id}/info
        `);
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setMedicationsError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setMedicationsError('Access denied. You do not have permission to view medications data.');
      } else {
        setMedicationsError(`Failed to load medications data: ${error.message}`);
      }
      
      setPurchaseMedications(null);
    } finally {
      setIsLoadingMedications(false);
    }
  };

  // Handle view medications button click
  const handleViewMedications = (purchaseId: number) => {
    if (expandedPurchaseId === purchaseId) {
      // If already expanded, hide the medications
      setExpandedPurchaseId(null);
      setPurchaseMedications(null);
      setMedicationsError('');
    } else {
      // Expand and fetch medications
      setExpandedPurchaseId(purchaseId);
      fetchPurchaseMedications(purchaseId);
    }
  };

  // Update purchase item quantity (for pending purchases)
  const updatePurchaseItemQuantity = async (itemId: number | string, quantity: number) => {
    const authToken = getAuthToken();
    if (!authToken) {
      setMedicationsError('Authentication token not found. Please log in again.');
      return;
    }
    try {
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/items/${itemId}`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ quantity })
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP ${response.status}`);
      }
      if (expandedPurchaseId != null) {
        await fetchPurchaseMedications(expandedPurchaseId);
      }
    } catch (err: any) {
      setMedicationsError(err?.message || 'Failed to update quantity.');
    }
  };

  // Handle auto-dismiss timer for response
  const startResponseTimer = () => {
    setResponseCountdown(8);
    const timer = setInterval(() => {
      setResponseCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setShowReceiveResponse(false);
          setReceiveResponse(null);
          return 8;
        }
        return prev - 1;
      });
    }, 1000);
    setResponseTimer(timer);
  };

  // Clear response timer
  const clearResponseTimer = () => {
    if (responseTimer) {
      clearInterval(responseTimer);
      setResponseTimer(null);
    }
  };


  // Handle marking purchase as received
  const handleMarkAsReceived = async (purchaseId: number) => {
    try {
      setReceivingPurchaseId(purchaseId);
      setReceiveError('');
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        setReceiveError('Authentication token not found. Please log in again.');
        setReceivingPurchaseId(null);
        return;
      }
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseId}/receive`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Store the response data
      setReceiveResponse({
        purchaseId,
        response: data,
        timestamp: new Date().toLocaleString()
      });
      setShowReceiveResponse(true);
      
      // Start auto-dismiss timer
      startResponseTimer();
      
      // Update the purchase status in the local state
      setPurchasesData(prevPurchases => 
        prevPurchases.map(purchase => 
          purchase.id === purchaseId 
            ? { ...purchase, status: 'received' }
            : purchase
        )
      );
      
    } catch (error) {
      
      // Check for different types of errors
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        setReceiveError(`
          CORS Error: Unable to connect to the receive API.
          
          Possible solutions:
          1. Check if your server is running
          2. Verify CORS is properly configured on the server
          3. Ensure the API endpoint is accessible
          4. Try refreshing the page
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseId}/receive
        `);
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setReceiveError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setReceiveError('Access denied. You do not have permission to mark purchases as received.');
      } else {
        setReceiveError(`Failed to mark purchase as received: ${error.message}`);
      }
    } finally {
      setReceivingPurchaseId(null);
    }
  };

  // Trigger load animation and fetch data
  useEffect(() => {
    setIsLoaded(true);
    fetchPurchasesData(currentPage, perPage);
  }, [currentPage, perPage]);
  useEffect(() => {
    const t = setTimeout(() => {
      fetchPurchasesData(currentPage, perPage);
    }, 200);
    return () => clearTimeout(t);
  }, [customFromDate, customToDate]);
  useEffect(() => {
    fetchAnalytics();
  }, [customFromDate, customToDate]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      clearResponseTimer();
    };
  }, []);

  // Sample purchase history data (fallback)
  const samplePurchasesData = [
    {
      id: 1,
      distributorName: 'MediCorp Pharmaceuticals',
      totalMedicines: 25,
      totalAmount: 1250.00,
      paid: 1000.00,
      pending: 250.00,
      purchaseDate: '2024-01-15',
      status: 'partial',
      medications: [
        { name: 'Paracetamol 500mg', quantity: 100, price: 5.99 },
        { name: 'Amoxicillin 250mg', quantity: 50, price: 12.50 },
        { name: 'Omeprazole 20mg', quantity: 30, price: 18.75 },
        { name: 'Metformin 500mg', quantity: 80, price: 8.25 },
        { name: 'Lisinopril 10mg', quantity: 60, price: 15.50 }
      ]
    },
    {
      id: 2,
      distributorName: 'HealthFirst Supplies',
      totalMedicines: 18,
      totalAmount: 890.00,
      paid: 890.00,
      pending: 0.00,
      purchaseDate: '2024-01-12',
      status: 'paid',
      medications: [
        { name: 'Aspirin 100mg', quantity: 200, price: 3.25 },
        { name: 'Ibuprofen 400mg', quantity: 150, price: 7.50 },
        { name: 'Cetirizine 10mg', quantity: 100, price: 9.99 },
        { name: 'Ranitidine 150mg', quantity: 75, price: 11.25 }
      ]
    },
    {
      id: 3,
      distributorName: 'PharmaPlus Distributors',
      totalMedicines: 32,
      totalAmount: 2100.00,
      paid: 0.00,
      pending: 2100.00,
      purchaseDate: '2024-01-10',
      status: 'pending',
      medications: [
        { name: 'Atorvastatin 20mg', quantity: 90, price: 22.50 },
        { name: 'Metoprolol 50mg', quantity: 120, price: 18.75 },
        { name: 'Losartan 50mg', quantity: 80, price: 16.25 },
        { name: 'Amlodipine 5mg', quantity: 100, price: 14.99 },
        { name: 'Hydrochlorothiazide 25mg', quantity: 60, price: 12.50 }
      ]
    },
    {
      id: 4,
      distributorName: 'Global Medical Supplies',
      totalMedicines: 15,
      totalAmount: 675.00,
      paid: 675.00,
      pending: 0.00,
      purchaseDate: '2024-01-08',
      status: 'paid',
      medications: [
        { name: 'Diazepam 5mg', quantity: 50, price: 25.00 },
        { name: 'Lorazepam 1mg', quantity: 40, price: 18.75 },
        { name: 'Clonazepam 0.5mg', quantity: 30, price: 22.50 }
      ]
    },
    {
      id: 5,
      distributorName: 'MediCorp Pharmaceuticals',
      totalMedicines: 22,
      totalAmount: 1100.00,
      paid: 550.00,
      pending: 550.00,
      purchaseDate: '2024-01-05',
      status: 'partial',
      medications: [
        { name: 'Simvastatin 20mg', quantity: 70, price: 19.99 },
        { name: 'Pravastatin 40mg', quantity: 50, price: 21.50 },
        { name: 'Rosuvastatin 10mg', quantity: 60, price: 24.75 },
        { name: 'Fluvastatin 80mg', quantity: 40, price: 23.25 }
      ]
    }
  ];

  const filteredPurchases = purchasesData.filter(purchase => {
    // Add null/undefined checks to prevent runtime errors
    if (!purchase || typeof purchase !== 'object') {
      return false;
    }

    const distributorName = purchase.distributor_name || '';
    const invoiceNo = purchase.invoice_no || '';
    const invoiceDate = purchase.invoice_date || '';
    const purchaseDate = purchase.created_at || '';

    const matchesSearch = distributorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoiceDate.includes(searchTerm) ||
      purchaseDate.includes(searchTerm);
    
    return matchesSearch;
  });


  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '0',
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Enhanced Animated Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(245, 158, 11, 0.06) 0%, transparent 50%),
          radial-gradient(circle at 60% 30%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 10% 70%, rgba(236, 72, 153, 0.04) 0%, transparent 50%)
        `,
        animation: 'backgroundFloat 25s ease-in-out infinite'
      }}></div>

      {/* Floating Particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        {/* Particle 1 */}
        <div style={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          background: 'rgba(16, 185, 129, 0.3)',
          borderRadius: '50%',
          top: '20%',
          left: '10%',
          animation: 'particleFloat1 15s ease-in-out infinite'
        }}></div>
        
        {/* Particle 2 */}
        <div style={{
          position: 'absolute',
          width: '12px',
          height: '12px',
          background: 'rgba(59, 130, 246, 0.25)',
          borderRadius: '50%',
          top: '60%',
          left: '85%',
          animation: 'particleFloat2 18s ease-in-out infinite'
        }}></div>
        
        {/* Particle 3 */}
        <div style={{
          position: 'absolute',
          width: '6px',
          height: '6px',
          background: 'rgba(245, 158, 11, 0.4)',
          borderRadius: '50%',
          top: '40%',
          left: '70%',
          animation: 'particleFloat3 20s ease-in-out infinite'
        }}></div>
        
        {/* Particle 4 */}
        <div style={{
          position: 'absolute',
          width: '10px',
          height: '10px',
          background: 'rgba(139, 92, 246, 0.2)',
          borderRadius: '50%',
          top: '80%',
          left: '20%',
          animation: 'particleFloat4 16s ease-in-out infinite'
        }}></div>
        
        {/* Particle 5 */}
        <div style={{
          position: 'absolute',
          width: '14px',
          height: '14px',
          background: 'rgba(236, 72, 153, 0.15)',
          borderRadius: '50%',
          top: '10%',
          left: '60%',
          animation: 'particleFloat5 22s ease-in-out infinite'
        }}></div>
        
        {/* Particle 6 */}
        <div style={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          background: 'rgba(16, 185, 129, 0.2)',
          borderRadius: '50%',
          top: '70%',
          left: '50%',
          animation: 'particleFloat6 19s ease-in-out infinite'
        }}></div>
      </div>

      {/* Geometric Shapes */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        {/* Triangle 1 */}
        <div style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderLeft: '15px solid transparent',
          borderRight: '15px solid transparent',
          borderBottom: '25px solid rgba(16, 185, 129, 0.1)',
          top: '15%',
          left: '25%',
          animation: 'shapeRotate1 30s linear infinite'
        }}></div>
        
        {/* Triangle 2 */}
        <div style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderLeft: '20px solid transparent',
          borderRight: '20px solid transparent',
          borderBottom: '35px solid rgba(59, 130, 246, 0.08)',
          top: '65%',
          left: '75%',
          animation: 'shapeRotate2 35s linear infinite'
        }}></div>
        
        {/* Circle */}
        <div style={{
          position: 'absolute',
          width: '30px',
          height: '30px',
          border: '2px solid rgba(245, 158, 11, 0.15)',
          borderRadius: '50%',
          top: '35%',
          left: '15%',
          animation: 'shapePulse 12s ease-in-out infinite'
        }}></div>
        
        {/* Square */}
        <div style={{
          position: 'absolute',
          width: '25px',
          height: '25px',
          border: '2px solid rgba(139, 92, 246, 0.12)',
          borderRadius: '4px',
          top: '55%',
          left: '80%',
          animation: 'shapeRotate3 25s linear infinite'
        }}></div>
      </div>

      {/* Content Container */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* Enhanced Modern Header - Fully Responsive */}
        <header className="responsive-header">
          <div className="header-container">
            {/* Back Button */}
            <button
              onClick={() => navigateTo('pharma-homepage')}
              className="back-button"
            >
              ←
            </button>

            {/* Center Heading */}
            <div className="header-title">
              <span className="responsive-title">Purchase History</span>
            </div>

            {/* Search Bar */}
            <div 
              className="search-filters-container"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flex: '0 0 auto',
                minWidth: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '100%' : 'none',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                marginLeft: isMobile ? '0' : 'auto',
                justifyContent: isMobile ? 'center' : 'flex-end',
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
              <div className="search-container" style={{ 
                flex: isMobile ? '1 1 100%' : '0 0 auto', 
                minWidth: isMobile ? '100%' : '250px',
                maxWidth: isMobile ? '100%' : 'none',
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box'
              }}> 
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="search-icon"
                >
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search purchases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content - Responsive */}
        <main style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: isMobile ? '1rem 0.75rem' : '2rem 1rem',
          transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
          opacity: isLoaded ? 1 : 0,
          transition: 'all 0.6s ease-out 0.2s'
        }}>
          <section style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Select Date Range</span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>From</span>
                    <input
                      type="date"
                      value={customFromDate}
                      onChange={(e) => { setCustomFromDate(e.target.value); setCurrentPage(1); }}
                      style={{
                        padding: '0.45rem 0.65rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        backgroundColor: 'white',
                        color: '#1f2937',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        minWidth: '140px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>To</span>
                    <input
                      type="date"
                      value={customToDate}
                      onChange={(e) => { setCustomToDate(e.target.value); setCurrentPage(1); }}
                      style={{
                        padding: '0.45rem 0.65rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        backgroundColor: 'white',
                        color: '#1f2937',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        minWidth: '140px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', alignSelf: 'flex-end' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>Export</span>
                    <button
                      type="button"
                      onClick={downloadPurchaseOrdersCsv}
                      disabled={isDownloadingCsv}
                      title="Download purchase orders for the selected date range as CSV"
                      style={{
                        padding: '0.45rem 0.75rem',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: isDownloadingCsv ? 'not-allowed' : 'pointer',
                        background: isDownloadingCsv
                          ? '#9ca3af'
                          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isDownloadingCsv ? 'Downloading…' : 'Download CSV'}
                    </button>
                    {csvDownloadError && (
                      <span style={{
                        fontSize: '0.68rem',
                        color: '#dc2626',
                        lineHeight: 1.25,
                        maxWidth: 'min(260px, 85vw)'
                      }}>
                        {csvDownloadError}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => applyDatePreset(3)}
                    style={{
                      padding: '0.3rem 0.55rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '9999px',
                      background: '#fff',
                      color: '#374151',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Last 3 Days
                  </button>
                  <button
                    onClick={() => applyDatePreset(7)}
                    style={{
                      padding: '0.3rem 0.55rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '9999px',
                      background: '#fff',
                      color: '#374151',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Last Week
                  </button>
                  <button
                    onClick={() => applyDatePreset(30)}
                    style={{
                      padding: '0.3rem 0.55rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '9999px',
                      background: '#fff',
                      color: '#374151',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Last Month
                  </button>
                </div>
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginTop: '1rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', marginBottom: '0.25rem' }}>{analyticsData?.total_orders ?? 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Orders</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(239, 68, 68, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ef4444', marginBottom: '0.25rem' }}>{analyticsData?.pending_orders ?? 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending Orders</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', marginBottom: '0.25rem' }}>{analyticsData?.confirmed_orders ?? 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confirmed Orders</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(59, 130, 246, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#3b82f6', marginBottom: '0.25rem' }}>{analyticsData?.received_orders ?? 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Received Orders</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(245, 158, 11, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f59e0b', marginBottom: '0.25rem' }}>₹{(Number(analyticsData?.total_purchases) || 0).toFixed(2)}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Purchases</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', marginBottom: '0.25rem' }}>₹{(Number(analyticsData?.total_settled) || 0).toFixed(2)}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Settled</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(239, 68, 68, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ef4444', marginBottom: '0.25rem' }}>₹{(Number(analyticsData?.total_unsettled) || 0).toFixed(2)}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Unsettled</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#6366f1', marginBottom: '0.25rem' }}>₹{(Number(analyticsData?.total_tax) || 0).toFixed(2)}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Tax</div>
              </div>
            </div>
          </section>
          {/* Hero Section - Responsive */}
          <section style={{
            marginBottom: '2rem'
          }}>
            {/* Loading State */}
            {isLoadingData && (
              <div style={{
                textAlign: 'center',
                padding: '4rem',
                color: '#6b7280'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #e5e7eb',
                  borderTop: '4px solid #10b981',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1.5rem auto'
                }}></div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Loading Purchases...
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#6b7280'
                }}>
                  Please wait while we fetch the data
                </p>
              </div>
            )}

            {/* Error State */}
            {apiError && !isLoadingData && (
              <div style={{
                textAlign: 'center',
                padding: '4rem',
                color: '#6b7280'
              }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1.5rem', opacity: 0.5 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  {apiError}
                </h3>
                <button
                  onClick={() => fetchPurchasesData(currentPage, perPage)}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Purchases Table */}
            <PharmaHistoryTable
              filteredPurchases={filteredPurchases}
              selectedPurchase={selectedPurchase}
              setSelectedPurchase={setSelectedPurchase}
              handleMarkAsReceived={handleMarkAsReceived}
              receivingPurchaseId={receivingPurchaseId}
              showReceiveResponse={showReceiveResponse}
              receiveResponse={receiveResponse}
              responseCountdown={responseCountdown}
              clearResponseTimer={clearResponseTimer}
              setShowReceiveResponse={setShowReceiveResponse}
              setReceiveResponse={setReceiveResponse}
              receiveError={receiveError}
              setReceiveError={setReceiveError}
              expandedPurchaseId={expandedPurchaseId}
              handleViewMedications={handleViewMedications}
              isLoadingMedications={isLoadingMedications}
              medicationsError={medicationsError}
              purchaseMedications={purchaseMedications}
              expandedPurchase={filteredPurchases.find((p: any) => p.id === expandedPurchaseId) ?? null}
              onUpdateItemQuantity={updatePurchaseItemQuantity}
              isLoadingData={isLoadingData}
              apiError={apiError}
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={setPerPage}
            />
          </section>
        </main>
      </div>

      <style>{`
        * {
          box-sizing: border-box;
        }

        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-webkit-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-moz-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input:-ms-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes backgroundFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes particleFloat1 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.3;
          }
          25% {
            transform: translate(20px, -30px) scale(1.2);
            opacity: 0.6;
          }
          50% {
            transform: translate(-15px, -20px) scale(0.8);
            opacity: 0.4;
          }
          75% {
            transform: translate(10px, 25px) scale(1.1);
            opacity: 0.5;
          }
        }

        @keyframes particleFloat2 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.25;
          }
          33% {
            transform: translate(-25px, 20px) scale(1.3);
            opacity: 0.5;
          }
          66% {
            transform: translate(30px, -15px) scale(0.7);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat3 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.4;
          }
          20% {
            transform: translate(15px, -25px) scale(1.4);
            opacity: 0.7;
          }
          40% {
            transform: translate(-20px, 10px) scale(0.6);
            opacity: 0.2;
          }
          60% {
            transform: translate(25px, 20px) scale(1.1);
            opacity: 0.5;
          }
          80% {
            transform: translate(-10px, -15px) scale(0.9);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat4 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.2;
          }
          30% {
            transform: translate(-30px, -20px) scale(1.5);
            opacity: 0.6;
          }
          60% {
            transform: translate(20px, 30px) scale(0.5);
            opacity: 0.1;
          }
        }

        @keyframes particleFloat5 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.15;
          }
          25% {
            transform: translate(35px, -25px) scale(1.6);
            opacity: 0.4;
          }
          50% {
            transform: translate(-25px, 35px) scale(0.4);
            opacity: 0.05;
          }
          75% {
            transform: translate(15px, -10px) scale(1.2);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat6 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.2;
          }
          40% {
            transform: translate(-20px, -30px) scale(1.3);
            opacity: 0.5;
          }
          80% {
            transform: translate(30px, 20px) scale(0.7);
            opacity: 0.1;
          }
        }

        @keyframes shapeRotate1 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: rotate(180deg) scale(1.2);
          }
          100% {
            transform: rotate(360deg) scale(1);
          }
        }

        @keyframes shapeRotate2 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: rotate(-180deg) scale(0.8);
          }
          100% {
            transform: rotate(-360deg) scale(1);
          }
        }

        @keyframes shapeRotate3 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          100% {
            transform: rotate(360deg) scale(1);
          }
        }

        @keyframes shapePulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.3;
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          50% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes successSlideIn {
          0% {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          50% {
            opacity: 0.8;
            transform: translateY(-5px) scale(1.02);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes checkmarkBounce {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.2) rotate(180deg);
            opacity: 0.8;
          }
          100% {
            transform: scale(1) rotate(360deg);
            opacity: 1;
          }
        }

        @keyframes backgroundPulse {
          0%, 100% {
            transform: scale(1) rotate(0deg);
            opacity: 0.03;
          }
          50% {
            transform: scale(1.1) rotate(180deg);
            opacity: 0.08;
          }
        }

        @keyframes cardSlideIn {
          0% {
            opacity: 0;
            transform: translateX(-20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.7;
          }
        }

        /* Hover effects for copy overlay */
        .copy-overlay {
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        pre:hover + .copy-overlay,
        .copy-overlay:hover {
          opacity: 1;
        }

        /* JSON formatting styles */
        pre {
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
        }

        /* Ensure proper JSON indentation */
        .json-response {
          font-family: "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
          white-space: pre-wrap !important;
          word-break: break-word !important;
        }

        /* Modern animations */
        @keyframes iconPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
          }
        }

        @keyframes statusPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
        }

        /* Modern copy overlay hover effects */
        .modern-copy-overlay {
          opacity: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateY(-4px);
        }

        .modern-copy-overlay:hover,
        div:hover .modern-copy-overlay {
          opacity: 1;
          transform: translateY(0);
        }

        /* Responsive Header Styles */
        .responsive-header {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%);
          backdrop-filter: blur(25px);
          border-bottom: 1px solid rgba(16, 185, 129, 0.1);
          padding: 1rem 0;
          position: sticky;
          top: 0;
          z-index: 100;
          transform: ${isLoaded ? 'translateY(0)' : 'translateY(-20px)'};
          opacity: ${isLoaded ? 1 : 0};
          transition: all 0.6s ease-out;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }

        .header-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
          display: flex;
          align-items: center;
          width: 100%;
          position: relative;
          gap: 1rem;
          justify-content: flex-start;
        }

        .back-button {
          background: linear-gradient(135deg, #1B5E20 0%, #0D4A14 100%);
          color: white;
          border: none;
          padding: 0.5rem;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(27, 94, 32, 0.3);
          transition: all 0.3s ease;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          min-width: 40px;
        }

        .back-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(27, 94, 32, 0.4);
        }

        .header-title {
          flex: 1;
          text-align: center;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          min-width: 0;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: auto;
        }

        /* Search Bar Responsive Styles */
        .search-container {
          position: relative;
          flex: 0 0 auto;
          min-width: 200px;
          max-width: 300px;
          box-sizing: border-box;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          z-index: 1;
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.75rem;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 0.9rem;
          background-color: white;
          color: #1f2937;
          outline: none;
          box-sizing: border-box;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          max-width: 100%;
        }

        .search-input:focus {
          border-color: #10b981;
          background-color: white;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .search-input::placeholder {
          color: #9ca3af;
          font-weight: 500;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .responsive-header {
            padding: 0.75rem 0;
            overflow-x: hidden;
            width: 100%;
            box-sizing: border-box;
          }

          .header-container {
            flex-direction: column;
            gap: 0.75rem;
            padding: 0 1rem;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            overflow-x: hidden;
          }


          .back-button {
            align-self: flex-start;
            order: 1;
          }

          .header-title {
            order: 2;
            font-size: 1rem;
            text-align: center;
            width: 100%;
            position: static !important;
            transform: none !important;
          }

          .search-filters-container {
            order: 3;
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-top: 0.5rem;
            justify-content: center !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            box-sizing: border-box;
            overflow-x: hidden;
            padding: 0;
          }

          .search-container {
            order: 1;
            width: 100% !important;
            max-width: 100% !important;
            min-width: unset !important;
            margin-top: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            box-sizing: border-box;
            padding: 0;
          }

          .search-input {
            padding: 0.875rem 0.875rem 0.875rem 2.5rem;
            font-size: 1rem;
            border-radius: 12px;
            min-height: 48px;
            box-sizing: border-box !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .search-icon {
            left: 0.875rem;
            width: 18px;
            height: 18px;
          }

          .search-input::placeholder {
            font-size: 0.9rem;
          }

          /* Mobile Medication Details - Clean Normal Design */
          div[style*="background: linear-gradient(135deg, rgba(255, 255, 255, 0.95)"] {
            background: #ffffff !important;
            border-radius: 8px !important;
            padding: 1rem !important;
            border: 1px solid #e5e7eb !important;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
            margin-bottom: 1rem !important;
            position: relative !important;
          }

          div[style*="position: absolute"] {
            display: none !important;
          }

          div[style*="fontSize: '1rem'"] {
            font-size: 1rem !important;
            font-weight: 600 !important;
            color: #1f2937 !important;
            margin-bottom: 0.75rem !important;
            padding-right: 0 !important;
            display: block !important;
            line-height: 1.4 !important;
          }

          div[style*="display: 'grid'"][style*="gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))'"] {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 0.5rem !important;
            margin-bottom: 0 !important;
          }

          div[style*="background: 'rgba(16, 185, 129, 0.05)'"],
          div[style*="background: 'rgba(59, 130, 246, 0.05)'"],
          div[style*="background: 'rgba(245, 158, 11, 0.05)'"],
          div[style*="background: 'rgba(239, 68, 68, 0.05)'"] {
            background: #f9fafb !important;
            border-radius: 6px !important;
            padding: 0.75rem !important;
            border: 1px solid #e5e7eb !important;
            text-align: center !important;
          }

          div[style*="background: 'rgba(239, 68, 68, 0.05)'"] {
            grid-column: 1 / -1 !important;
            margin-top: 0.5rem !important;
          }

          div[style*="fontSize: '0.7rem'"][style*="color: '#6b7280'"] {
            font-size: 0.7rem !important;
            font-weight: 500 !important;
            color: #6b7280 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.05em !important;
            margin-bottom: 0.25rem !important;
            display: block !important;
          }

          div[style*="fontSize: '0.9rem'"][style*="fontWeight: '700'"] {
            font-size: 0.9rem !important;
            font-weight: 600 !important;
            margin: 0 !important;
            color: #1f2937 !important;
            line-height: 1.2 !important;
          }

          div[style*="color: '#10b981'"] {
            color: #10b981 !important;
          }

          div[style*="color: '#3b82f6'"] {
            color: #3b82f6 !important;
          }

          div[style*="color: '#f59e0b'"] {
            color: #f59e0b !important;
          }

          div[style*="color: '#ef4444'"] {
            color: #ef4444 !important;
          }

          /* Hide additional details section on mobile */
          div[style*="display: 'grid'"][style*="gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))'"] {
            display: none !important;
          }
        }

        @media (max-width: 480px) {
          .header-container {
            padding: 0 0.75rem;
            gap: 0.5rem;
          }

          .responsive-header {
            padding: 0.5rem 0;
          }

          .search-filters-container {
            gap: 0.5rem !important;
            justify-content: center !important;
            align-items: center !important;
          }

          .search-container {
            margin-top: 0.25rem;
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
          }

          .search-input {
            padding: 1rem 1rem 1rem 2.25rem;
            font-size: 1rem;
            border-radius: 14px;
            min-height: 52px;
          }

          .search-icon {
            left: 0.75rem;
            width: 20px;
            height: 20px;
          }

          .search-input::placeholder {
            font-size: 0.95rem;
          }

          /* Normal mobile medication details for small screens */
          div[style*="background: linear-gradient(135deg, rgba(255, 255, 255, 0.95)"] {
            padding: 0.875rem !important;
            margin-bottom: 0.875rem !important;
            border-radius: 6px !important;
          }

          div[style*="fontSize: '1rem'"] {
            font-size: 0.95rem !important;
            margin-bottom: 0.625rem !important;
          }

          div[style*="display: 'grid'"][style*="gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))'"] {
            grid-template-columns: 1fr !important;
            gap: 0.5rem !important;
          }

          div[style*="background: 'rgba(16, 185, 129, 0.05)'"],
          div[style*="background: 'rgba(59, 130, 246, 0.05)'"],
          div[style*="background: 'rgba(245, 158, 11, 0.05)'"],
          div[style*="background: 'rgba(239, 68, 68, 0.05)'"] {
            padding: 0.625rem !important;
            border-radius: 4px !important;
          }

          div[style*="fontSize: '0.9rem'"][style*="fontWeight: '700'"] {
            font-size: 0.85rem !important;
          }
        }

        @media (min-width: 769px) {
          .header-container {
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
          }

          .back-button {
            order: 1;
            flex-shrink: 0;
            margin-right: auto;
          }

          .header-title {
            order: 2;
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            text-align: center;
            font-size: 1.25rem;
            width: auto;
          }

          .search-filters-container {
            order: 3;
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .search-container {
            order: 3;
            min-width: 250px;
            max-width: 350px;
            flex: 0 0 auto;
          }

          .search-input {
            padding: 0.75rem 0.75rem 0.75rem 2.75rem;
            font-size: 0.9rem;
            border-radius: 10px;
            min-height: 44px;
          }

          .search-icon {
            left: 1rem;
            width: 20px;
            height: 20px;
          }

          .search-input::placeholder {
            font-size: 0.85rem;
          }
        }

        @media (max-width: 768px) {
          .responsive-title {
            font-size: 1rem !important;
          }
          
          .responsive-card-layout {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          
          .mobile-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .purchase-title-section {
            flex: 1;
            min-width: 0;
          }
          
          .purchase-title {
            font-size: 1rem !important;
            font-weight: 700 !important;
            color: #1f2937 !important;
            margin: 0 0 0.25rem 0 !important;
            word-break: break-word;
          }
          
          .purchase-id {
            background: #f3f4f6 !important;
            color: #6b7280 !important;
            padding: 0.25rem 0.5rem !important;
            border-radius: 6px !important;
            font-size: 0.7rem !important;
            font-weight: 600 !important;
            font-family: monospace !important;
            display: inline-block;
          }
          
          .purchase-details-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 0.75rem;
            margin-bottom: 1rem;
          }
          
          .detail-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem;
            background: rgba(248, 250, 252, 0.8);
            border-radius: 8px;
            border: 1px solid rgba(226, 232, 240, 0.5);
          }
          
          .detail-label {
            font-size: 0.8rem;
            color: #6b7280;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          
          .detail-value {
            font-size: 0.85rem;
            color: #374151;
            font-weight: 500;
            text-align: right;
            word-break: break-word;
          }
          
          .financial-metrics {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-bottom: 1.5rem;
          }
          
          .metric-card {
            padding: 1rem;
            border-radius: 12px;
            text-align: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          
          .total-amount {
            background: rgba(59, 130, 246, 0.05);
            border: 1px solid rgba(59, 130, 246, 0.1);
          }
          
          .payment-mode {
            background: rgba(16, 185, 129, 0.05);
            border: 1px solid rgba(16, 185, 129, 0.1);
          }
          
          .metric-value {
            font-size: 1.2rem !important;
            font-weight: 900 !important;
            margin-bottom: 0.25rem !important;
          }
          
          .total-amount .metric-value {
            color: #3b82f6 !important;
          }
          
          .payment-mode .metric-value {
            color: #10b981 !important;
          }
          
          .metric-label {
            font-size: 0.75rem !important;
            color: #6b7280 !important;
            font-weight: 600 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.05em !important;
          }
          
          .action-buttons {
            display: flex;
            gap: 0.75rem;
            width: 100%;
          }
          
          .action-btn {
            flex: 1;
            padding: 0.875rem 1rem !important;
            border-radius: 12px !important;
            font-size: 0.85rem !important;
            font-weight: 600 !important;
            border: none !important;
            cursor: pointer !important;
            transition: all 0.3s ease !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 0.5rem !important;
            min-height: 48px;
          }
          
          .view-btn {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
            color: white !important;
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
          }
          
          .receive-btn {
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
            color: white !important;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
          }
          
          .receive-btn.disabled {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%) !important;
            opacity: 0.6 !important;
            cursor: not-allowed !important;
          }
          
          .loading-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-top: 2px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        }

        @media (max-width: 480px) {
          /* Small Mobile Search Bar */
          .search-container {
            min-width: 120px;
            max-width: 100%;
            margin-top: 0.5rem;
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
          }

          .search-input {
            padding: 1rem 1rem 1rem 2.25rem;
            font-size: 1rem;
            border-radius: 14px;
            min-height: 52px;
            width: 100% !important;
            max-width: 100% !important;
          }

          .search-icon {
            left: 0.75rem;
            width: 20px;
            height: 20px;
          }

          .search-input::placeholder {
            font-size: 0.95rem;
          }

          .financial-metrics {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }
          
          .action-buttons {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .action-btn {
            width: 100%;
          }
        }

        @media (min-width: 769px) {
          /* Desktop Search Bar */
          .search-container {
            min-width: 250px;
            max-width: 350px;
            flex: 0 0 auto;
            order: 3;
            margin-top: 0;
            margin-left: auto;
          }

          .search-input {
            padding: 0.75rem 0.75rem 0.75rem 2.75rem;
            font-size: 0.9rem;
            border-radius: 10px;
            min-height: 44px;
          }

          .search-icon {
            left: 1rem;
            width: 20px;
            height: 20px;
          }

          .search-input::placeholder {
            font-size: 0.85rem;
          }

          /* Desktop Card Layout - Improved Spacing */
          .responsive-card-layout {
            display: flex;
            align-items: center;
            gap: 2.5rem;
            padding: 0.5rem 0;
          }
          
          .mobile-header {
            flex: 0 0 320px;
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            padding: 0.5rem 0;
          }
          
          .purchase-title-section {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 0.5rem;
          }
          
          .purchase-title {
            font-size: 1.2rem !important;
            font-weight: 700 !important;
            color: #1f2937 !important;
            margin: 0 !important;
            line-height: 1.4;
          }
          
          .purchase-id {
            background: #f3f4f6 !important;
            color: #6b7280 !important;
            padding: 0.375rem 0.75rem !important;
            border-radius: 8px !important;
            font-size: 0.75rem !important;
            font-weight: 600 !important;
            font-family: monospace !important;
            display: inline-block;
          }
          
          .purchase-details-grid {
            display: none;
          }
          
          .financial-metrics {
            flex: 1;
            display: flex;
            gap: 2rem;
            align-items: center;
            padding: 0.5rem 0;
          }
          
          .metric-card {
            padding: 1.25rem 1.5rem;
            border-radius: 14px;
            text-align: center;
            min-width: 140px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            border: 1px solid rgba(0, 0, 0, 0.05);
          }
          
          .total-amount {
            background: rgba(59, 130, 246, 0.08);
            border: 1px solid rgba(59, 130, 246, 0.15);
          }
          
          .payment-mode {
            background: rgba(16, 185, 129, 0.08);
            border: 1px solid rgba(16, 185, 129, 0.15);
          }
          
          .metric-value {
            font-size: 1.5rem !important;
            font-weight: 900 !important;
            margin-bottom: 0.375rem !important;
            line-height: 1.2;
          }
          
          .total-amount .metric-value {
            color: #3b82f6 !important;
          }
          
          .payment-mode .metric-value {
            color: #10b981 !important;
            font-size: 1.3rem !important;
          }
          
          .metric-label {
            font-size: 0.8rem !important;
            color: #6b7280 !important;
            font-weight: 600 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.05em !important;
            line-height: 1.3;
          }
          
          .action-buttons {
            flex: 0 0 220px;
            display: flex;
            gap: 1rem;
            padding: 0.5rem 0;
          }
          
          .action-btn {
            flex: 1;
            padding: 0.875rem 0.75rem !important;
            border-radius: 12px !important;
            font-size: 0.85rem !important;
            font-weight: 600 !important;
            border: none !important;
            cursor: pointer !important;
            transition: all 0.3s ease !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 0.5rem !important;
            min-height: 48px;
          }

          .view-btn {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
            color: white !important;
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
          }

          .receive-btn {
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
            color: white !important;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
          }

          .receive-btn.disabled {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%) !important;
            opacity: 0.6 !important;
            cursor: not-allowed !important;
          }

          /* Desktop Purchase Card Improvements */
          .purchase-card {
            padding: 2.5rem !important;
            margin-bottom: 2rem !important;
            border-radius: 20px !important;
          }

          /* Desktop Status Badge */
          .status-badge {
            padding: 0.6rem 1.2rem !important;
            border-radius: 14px !important;
            font-size: 0.85rem !important;
            font-weight: 600 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PurchaseHistory;
